import { useEffect } from 'react';
import { yieldToMainBackground } from 'utils/schedulerUtils';

const listenerCallbacks = new WeakMap();

let observer;

async function handleIntersections(entries) {
  const promises = entries.map(async entry => {
    if (listenerCallbacks.has(entry.target)) {
      const cb = listenerCallbacks.get(entry.target);

      if (entry.isIntersecting || entry.intersectionRatio > 0) {
        observer.unobserve(entry.target);
        listenerCallbacks.delete(entry.target);
        await yieldToMainBackground();
        cb();
      }
    }
  });

  await Promise.all(promises);
}

function getIntersectionObserver() {
  if (observer === undefined) {
    observer = new IntersectionObserver(handleIntersections, {
      rootMargin: '300px',
      threshold: '0.15',
    });
  }
  return observer;
}

export default function useIntersection(elem, callback) {
  useEffect(() => {
    if (!('IntersectionObserver' in window) || elem.current === undefined) {
      callback();
      return () => {};
    }
    if (
      typeof window !== 'undefined' &&
      window.TimesApps &&
      window.TimesApps.globalIntersectObserver &&
      window.TimesApps.globalIntersectObserver.disconnect
    ) {
      window.TimesApps.globalIntersectObserver.disconnect();
    }
    const target = elem.current;
    const _observer = getIntersectionObserver();
    listenerCallbacks.set(target, callback);
    _observer.observe(target);

    return () => {
      listenerCallbacks.delete(target);
      _observer.unobserve(target);
      if (
        typeof window !== 'undefined' &&
        window.TimesApps &&
        window.TimesApps.globalIntersectObserver &&
        window.TimesApps.globalIntersectObserver.disconnect
      ) {
        window.TimesApps.globalIntersectObserver.disconnect();
      }
    };
  }, []);
}
